import * as React from 'react';
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  RowModel,
  Row,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'components/ui/table';
import { cn } from 'lib/utils';

import { ExportExcel } from '../ExportExcel';
import { Skeleton } from './data-table-skeleton';
import { Button } from '../ButtonGs';
import { DataTablePagination } from './data-table-pagination';
import { DataTableViewOptions } from './data-table-view-options';
import { Input } from '../input';
import { getPinningStyles, pinColumns } from './utils';

type DataTableProps<TData, TValue = unknown> = {
  id?: string;
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  filterBy?: string;
  exportTable?: boolean;
  isLoading?: boolean;
  showControlColumns?: boolean;
  pageSize?: number;
  selectable?: boolean;
  selectionAction?: (data: RowModel<TData>) => void;
  selectionActionLabel?: string;
  onRowClick?: (row: Row<TData>) => void;
  renderTopBar?: (props: RenderDataTableTopBarProps<TData>) => React.ReactNode;
  tableWrapperClassName?: string;
  tableWrapperStyle?: React.CSSProperties;
  /**
   * For better results, set the size for each column
   */
  leftPinnedColumns?: string[];
  /**
   * For better results, set the size for each column
   */
  rightPinnedColumns?: string[];
} & Omit<React.HTMLAttributes<HTMLTableElement>, 'className'>

export type RenderDataTableTopBarProps<T> = {
  selectedRows: RowModel<T>;
  totalRows: number;
}

export type DataTableRef = {
  clearSelection: () => void;
}

export const DataTable = React.forwardRef<DataTableRef, DataTableProps<any>>(
  <TData, TValue>(
    {
      columns,
      data,
      filterBy,
      exportTable = false,
      isLoading = false,
      showControlColumns = true,
      pageSize = 10,
      selectable = false,
      selectionAction,
      selectionActionLabel,
      onRowClick,
      renderTopBar,
      tableWrapperClassName,
      tableWrapperStyle,
      leftPinnedColumns,
      rightPinnedColumns,
      ...tableProps
    }: DataTableProps<TData, TValue>,
    ref: React.Ref<DataTableRef>,
  ) => {

    const [sorting, setSorting] = React.useState<SortingState>([]);
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
      [],
    );
    const [columnVisibility, setColumnVisibility] =
      React.useState<VisibilityState>({});
    const [rowSelection, setRowSelection] = React.useState({});

    const hasPinnedColumns = React.useMemo(() => {
      return leftPinnedColumns?.length || rightPinnedColumns?.length;
    }, [leftPinnedColumns?.length, rightPinnedColumns?.length]);

    const table = useReactTable({
      data,
      columns,
      getCoreRowModel: getCoreRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      onColumnFiltersChange: setColumnFilters,
      onColumnVisibilityChange: setColumnVisibility,
      onSortingChange: setSorting,
      onRowSelectionChange: setRowSelection,
      state: {
        sorting,
        columnFilters,
        columnVisibility,
        rowSelection,
      },
      initialState: {
        pagination: {
          pageSize: pageSize,
        },
      },
    });

    const TopBarComponent = React.useMemo(() => {
      if (!renderTopBar) return null;
      const selectedRows = table.getFilteredSelectedRowModel();
      const totalRows = table.getFilteredRowModel().rows.length;
      return renderTopBar({
        selectedRows,
        totalRows,
      });
    }, [renderTopBar, rowSelection]);

    React.useImperativeHandle(ref, () => ({
      clearSelection: () => {
        setRowSelection({});
      },
    }), []);

    React.useEffect(() => {
      if (leftPinnedColumns?.length || rightPinnedColumns?.length) {
        pinColumns({
          headerGroups: table.getHeaderGroups(),
          leftPinnedColumns,
          rightPinnedColumns,
        });
      }
    }, [leftPinnedColumns, rightPinnedColumns]);


    if (isLoading) {
      return <Skeleton />;
    }

    if (!data.length) {
      return (
        <div className="flex items-center justify-center h-24 text-center font-sans">
          Sem resultados.
        </div>
      );
    }

    return (
      <div>
        <div className="w-full mb-4 flex flex-col md:flex-row gap-3 items-center justify-between">
          {filterBy && (
            <Input
              placeholder={`Filtrar ${filterBy.toLowerCase()} ...`}
              value={
                (table.getColumn(filterBy)?.getFilterValue() as string) ?? ''
              }
              onChange={(event) =>
                table.getColumn(filterBy)?.setFilterValue(event.target.value)
              }
              className="max-w-sm focus-visible:ring-primary-500"
            />
          )}
          {TopBarComponent}
          {selectionAction && (
            <Button.Root
              onClick={() => selectionAction(table.getFilteredSelectedRowModel())}
              size="sm"
              disabled={!table.getFilteredSelectedRowModel().rows.length}
            >
              {selectionActionLabel ?? 'Action'} (
              {table.getFilteredSelectedRowModel().rows.length}/
              {table.getFilteredRowModel().rows.length} )
            </Button.Root>
          )}
          <div className="flex flex-1 gap-3 w-full justify-end">
            {exportTable && <ExportExcel data={data} />}
            {showControlColumns && <DataTableViewOptions table={table} />}
          </div>
        </div>
        <div className="rounded-md border bg-white">
          <Table {...tableProps}
                 wrapperClassName={cn('flex-1 overflow-y-auto', tableWrapperClassName)}
                 wrapperStyle={tableWrapperStyle}
                 className={'relative bg-background'}
                 style={{
                   ...(hasPinnedColumns && {
                    /*
                      Adding some pixels to the total size to avoid a visual bug
                      described on the issue https://github.com/TanStack/table/issues/5783
                     */
                     width: table.getTotalSize() + 40
                   })
                 }}
          >
            <TableHeader className={'sticky top-0 z-10 bg-inherit'}>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead
                        key={header.id}
                        colSpan={header.colSpan}
                        style={{ ...getPinningStyles(header.column) }}
                      >
                        <div className={'whitespace-nowrap'}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                        </div>
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody className="text-xs font-sans flex-1 overflow-auto">
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && 'selected'}
                    onClick={() => onRowClick?.(row)}
                    className={cn(
                      onRowClick && 'cursor-pointer',
                      (leftPinnedColumns?.length || rightPinnedColumns?.length) && 'hover:bg-muted/100',
                    )}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        style={{ ...getPinningStyles(cell.column) }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center font-sans"
                  >
                    Sem resultados.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-start space-x-2 py-4">
          {selectable && (
            <div className="flex-1 text-sm text-muted-foreground">
              {table.getFilteredSelectedRowModel().rows.length} of{' '}
              {table.getFilteredRowModel().rows.length} linha(s) selecionada(s).
            </div>
          )}
          <DataTablePagination table={table} />
        </div>
      </div>
    );
  });
DataTable.displayName = 'DataTable';

